import { createSlice } from '@reduxjs/toolkit';

import { createHospital, fetchAdminHospitalById, fetchAdminHospitals, updateHospital } from './hospital-admin-api';
import type { AdminHospital } from '@/types/hospital-admin';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { logout } from '../auth/logout';
export interface HospitalAdminState {
    hospitals: AdminHospital[];
    currentHospital: AdminHospital | null;
    total: number;
    page: number;
    limit: number;
    hospitalsLoading: boolean;
    hospitalsError: string | null;
    currentHospitalLoading: boolean;
    currentHospitalError: string | null;
    createHospitalLoading: boolean;
    createHospitalError: string | null;
    updateHospitalLoading: boolean;
    updateHospitalError: string | null;
}

const initialState: HospitalAdminState = {
    hospitals: [],
    currentHospital: null,
    total: 0,
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
    hospitalsLoading: false,
    hospitalsError: null,
    currentHospitalLoading: false,
    currentHospitalError: null,
    createHospitalLoading: false,
    createHospitalError: null,
    updateHospitalLoading: false,
    updateHospitalError: null
};

const hospitalAdminSlice = createSlice({
    name: 'hospitalAdmin',
    initialState,
    reducers: {
        clearHospitalAdminError: (state) => {
            state.hospitalsError = null;
            state.currentHospitalError = null;
            state.createHospitalError = null;
            state.updateHospitalError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdminHospitals.pending, (state) => {
                state.hospitalsLoading = true;
                state.hospitalsError = null;
            })
            .addCase(fetchAdminHospitals.fulfilled, (state, action) => {
                state.hospitalsLoading = false;
                state.hospitals = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.limit = action.payload.limit;
                state.hospitalsError = null;
            })
            .addCase(fetchAdminHospitals.rejected, (state, action) => {
                state.hospitalsLoading = false;
                state.hospitalsError = action.payload ?? 'Failed to fetch hospitals list';
            })
            .addCase(fetchAdminHospitalById.pending, (state) => {
                state.currentHospitalLoading = true;
                state.currentHospitalError = null;
            })
            .addCase(fetchAdminHospitalById.fulfilled, (state, action) => {
                state.currentHospitalLoading = false;
                state.currentHospitalError = null;
                state.currentHospital = action.payload;
                state.hospitals = state.hospitals.some((hospital) => hospital.id === action.payload.id)
                    ? state.hospitals.map((hospital) => (hospital.id === action.payload.id ? { ...hospital, ...action.payload } : hospital))
                    : [action.payload, ...state.hospitals];
            })
            .addCase(fetchAdminHospitalById.rejected, (state, action) => {
                state.currentHospitalLoading = false;
                state.currentHospitalError = action.payload ?? 'Failed to fetch hospital details';
            })
            .addCase(createHospital.pending, (state) => {
                state.createHospitalLoading = true;
                state.createHospitalError = null;
            })
            .addCase(createHospital.fulfilled, (state, action) => {
                state.createHospitalLoading = false;
                state.createHospitalError = null;
            })
            .addCase(createHospital.rejected, (state, action) => {
                state.createHospitalLoading = false;
                state.createHospitalError = action.payload ?? 'Failed to create hospital';
            })
            .addCase(updateHospital.pending, (state) => {
                state.updateHospitalLoading = true;
                state.updateHospitalError = null;
            })
            .addCase(updateHospital.fulfilled, (state, action) => {
                state.updateHospitalLoading = false;
                state.updateHospitalError = null;
                const { id: updatedHospitalId, ...requestedUpdates } = action.meta.arg;
                state.hospitals = state.hospitals.map((hospital) =>
                    hospital.id === updatedHospitalId ? { ...hospital, ...action.payload, ...requestedUpdates } : hospital
                );
                if (state.currentHospital?.id === updatedHospitalId) {
                    state.currentHospital = { ...state.currentHospital, ...action.payload, ...requestedUpdates };
                }
            })
            .addCase(updateHospital.rejected, (state, action) => {
                state.updateHospitalLoading = false;
                state.updateHospitalError = action.payload ?? 'Failed to update hospital';
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearHospitalAdminError } = hospitalAdminSlice.actions;
export default hospitalAdminSlice.reducer;
